import React, { useEffect, useMemo, useRef } from "react";
import PageHeader from "../../../../components/Common/PageHeader";
import {
  Button,
  FormFieldSet,
  PageTitle,
  useFormHandler,
  Divider,
  showToast,
} from "../../../../components";
import {
  cityStateListOptions,
  countryOptions,
} from "../../../../assets/data/projectData";
import { ContactType, Gender } from "../../../../assets/data/enumData";
import { useDispatch, useSelector } from "react-redux";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import {
  addContactStart,
  addToDraft,
  getClientOneStart,
  updateContactStart,
} from "../../../../redux/slices/client/clientSlice";
import useDropdownData from "../../../../hooks/useDropdownData";
import { filterEmptyValues } from "../../../../utils/filterEmptyValues";
import { debounce } from "../../../../utils";

const ContactComponent = ({
  contact,
  index,
  formData,
  handleRemove,
  handleChange,
  isEditable,
  errors,
}) => {
  const containerRef = useRef(null);

  const { data: countryOptions, handleScroll: handleCountriesScroll } =
    useDropdownData("countriesUsAndCanada");

  const countryId = countryOptions.find(
    (item) => item.label === contact.country
  )?.id;

  const {
    data: stateOptions,
    searchValue: stateSearch,
    handleSearch: handleStateSearch,
    resetSearch: resetStateSearch,
    handleScroll: handleStateScroll,
  } = useDropdownData(
    "states",
    10,
    { country: contact.country },
    { countrySearch: countryId }
  );

  const {
    data: cityOptions,
    searchValue: citySearch,
    handleSearch: handleCitySearch,
    resetSearch: resetCitySearch,
    handleScroll: handleCityScroll,
  } = useDropdownData(
    "cities",
    10,
    { country: contact.country, state: contact.state },
    { countrySearch: countryId, stateSearch: contact.state }
  );

  return (
    <div key={index}>
      {index > 0 && (
        <div className="my-6">
          <Divider />
          <div className="flex justify-end">
            <Button
              type="icon"
              icon="delete"
              size="sm"
              color="info"
              variant="text"
              className="mt-2"
              onClick={() => handleRemove(index)}
              disabled={!isEditable}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 md:gap-x-8 lg:gap-x-8 gap-y-6 max-w-7xl">
        <FormFieldSet
          type="text"
          title={"Representative Name"}
          placeholder="Enter Representative Name"
          value={contact.representativeName}
          onChange={(value) =>
            handleChange(`contacts[${index}].representativeName`, value)
          }
          error={errors.contacts?.[index]?.representativeName}
          disabled={!isEditable}
        />
        {/* <FormFieldSet
          type="text"
          title={"Job Title"}
          placeholder="Enter Job Title"
          value={contact.jobTitle}
          onChange={(value) =>
            handleChange(`contacts[${index}].jobTitle`, value)
          }
          error={errors.contacts?.[index]?.jobTitle}
          disabled={!isEditable}
        /> */}
        <FormFieldSet
          type="jobTitle"
          title="Job Title"
          placeholder="Enter Job Title"
          value={contact.jobTitle}
          onChange={(value) =>
            handleChange(`contacts[${index}].jobTitle`, value?.value ?? value)
          }
          error={errors.contacts?.[index]?.jobTitle}
          showSearch
          showButton
          disabled={!isEditable}
        />
        <FormFieldSet
          type="contactType"
          title={"Contact Type"}
          placeholder="Select Contact Type"
          options={ContactType}
          value={contact.contactType}
          onChange={(value) => {
            handleChange(
              `contacts[${index}].contactType`,
              value?.value ?? value
            );
          }}
          error={errors.contacts?.[index]?.contactType}
          showButton
          showSearch
          disabled={!isEditable}
        />
        <FormFieldSet
          type="select"
          title={"Gender"}
          placeholder="Select Gender"
          options={Gender}
          value={contact.gender}
          onChange={(value) => handleChange(`contacts[${index}].gender`, value)}
          error={errors.contacts?.[index]?.gender}
          // showButton
          // showSearch
          disabled={!isEditable}
        />
        <FormFieldSet
          type="text"
          title={"Email"}
          placeholder="Enter Email"
          value={contact.email}
          onChange={(value) => handleChange(`contacts[${index}].email`, value)}
          error={errors.contacts?.[index]?.email}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="phone"
          title={"Contact Number"}
          placeholder="Enter Contact"
          value={contact.contactNumber}
          code={contact.contactNumberCountryCode}
          onChange={(fullValue, details) => {
            handleChange(`contacts[${index}].contactNumber`, details.number);
            handleChange(
              `contacts[${index}].contactNumberCountryCode`,
              details.countryCode
            );
          }}
          error={errors.contacts?.[index]?.contactNumber}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="phone"
          title={"Work Phone"}
          placeholder="Enter Contact"
          value={contact.workPhone}
          code={contact.workPhoneCountryCode}
          onChange={(fullValue, details) => {
            handleChange(`contacts[${index}].workPhone`, details.number);
            handleChange(
              `contacts[${index}].workPhoneCountryCode`,
              details.countryCode
            );
            //  setErrors({
            //       ...errors,
            //       contactNumber: details.number
            //         ? details?.error?.valid === true
            //           ? ""
            //           : "Mobile number invalid"
            //         : "",
            //     });
          }}
          error={errors.contacts?.[index]?.workPhone}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="phone"
          title={"Home Phone"}
          placeholder="Enter Contact"
          value={contact.homePhone}
          code={contact.homePhoneCountryCode}
          onChange={(fullValue, details) => {
            handleChange(`contacts[${index}].homePhone`, details.number);
            handleChange(
              `contacts[${index}].homePhoneCountryCode`,
              details.countryCode
            );
          }}
          error={errors.contacts?.[index]?.homePhone}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="datepicker"
          title={"Date of Birth"}
          placeholder="Enter Date of Birth"
          maxDate={new Date()}
          value={contact.dateOfBirth}
          onChange={(value) =>
            handleChange(`contacts[${index}].dateOfBirth`, value)
          }
          error={errors.contacts?.[index]?.dateOfBirth}
          disabled={!isEditable}
          showDropdown={false}
        />
        <FormFieldSet
          type="text"
          title={"Address Line 1"}
          placeholder="Enter Address"
          value={contact.addressLineOne}
          onChange={(value) =>
            handleChange(`contacts[${index}].addressLineOne`, value)
          }
          error={errors.contacts?.[index]?.addressLineOne}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="text"
          title={"Address Line 2"}
          placeholder="Enter Address"
          value={contact.addressLineTwo}
          onChange={(value) =>
            handleChange(`contacts[${index}].addressLineTwo`, value)
          }
          error={errors.contacts?.[index]?.addressLineTwo}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="select"
          title="Country"
          placeholder="Select Country"
          options={countryOptions}
          value={contact.country}
          onChange={(value) =>
            handleChange(`contacts[${index}].country`, value)
          }
          error={errors.contacts?.[index]?.country}
          disabled={!isEditable}
        />
        <FormFieldSet
          type="select"
          title="State"
          keyname="states"
          placeholder="Select State"
          options={stateOptions}
          searchValue={stateSearch}
          onSearch={handleStateSearch}
          onClose={resetStateSearch}
          showSearch
          value={contact.state}
          onChange={(value) => handleChange(`contacts[${index}].state`, value)}
          error={errors.contacts?.[index]?.state}
          disabled={!isEditable || !contact.country}
          refData={containerRef}
          handleScroll={() => handleStateScroll(containerRef)}
        />
        <FormFieldSet
          type="select"
          title="City"
          keyname="cities"
          placeholder="Select City"
          options={cityOptions}
          searchValue={citySearch}
          onSearch={handleCitySearch}
          onClose={resetCitySearch}
          showSearch
          value={contact.city}
          onChange={(value) => handleChange(`contacts[${index}].city`, value)}
          error={errors.contacts?.[index]?.city}
          disabled={!isEditable || !contact.country || !contact.state}
          refData={containerRef}
          handleScroll={() => handleCityScroll(containerRef)}
        />
        <FormFieldSet
          type="text"
          title={"Zip Code"}
          placeholder="Enter Zipcode"
          value={contact.zipCode}
          onChange={(value) =>
            handleChange(`contacts[${index}].zipCode`, value)
          }
          error={errors.contacts?.[index]?.zipCode}
          disabled={!isEditable}
        />
      </div>
    </div>
  );
};

const Contacts = ({ setActiveTab, isEditable, editClient = "", clientId }) => {
  const dispatch = useDispatch();
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const clientProfileUiid = useSelector(
    (state) => state.client.clientProfileUiid
  );
  const clientDetails = useSelector(
    (state) => state.client.currentClient.clientDetails
  );

  const handleBack = () => {
    setTimeout(() => setActiveTab((prev) => prev - 1), 500);
  };
  useEffect(() => {
    if (clientProfileUiid) {
      dispatch(
        getClientOneStart({
          clientUiid: clientProfileUiid,
          retrieve: `contacts`,
          ref: `Contact`,
        })
      );
    }
  }, [clientId]);

  const {
    formData,
    errors,
    handleChange,
    validateAllFields,
    setFormData,
    setErrors,
  } = useFormHandler(
    {
      contacts: [
        {
          uiid: "",
          representativeName: "",
          jobTitle: "",
          contactType: "",
          gender: "",
          email: "",
          contactNumber: "",
          contactNumberCountryCode: "",
          workPhone: "",
          workPhoneCountryCode: "",
          homePhone: "",
          homePhoneCountryCode: "",
          dateOfBirth: "",
          addressLineOne: "",
          addressLineTwo: "",
          country: "",
          state: "",
          city: "",
          zipCode: "",
        },
      ],
    },
    {
      contacts: [
        {
          // representativeName: { type: "text", required: true },
          // jobTitle: { type: "select", required: true },
          // gender: { type: "select", required: true },
          // email: { type: "email", required: true },
          contactNumber: { type: "phone", minLength: 9, maxLength: 15 },
          workPhone: { type: "phone", minLength: 9, maxLength: 15 },
          homePhone: { type: "phone", minLength: 9, maxLength: 15 },
        },
      ],
    }
  );
  //handles changes from store - prefill
  useEffect(() => {
    // if (clientProfileUiid) {
    if (true) {
      if (
        (clientDetails?.contacts &&
          Array.isArray(clientDetails.contacts) &&
          clientDetails.contacts.length > 0) ||
        Object.keys(clientDetails)
      ) {
        clientDetails?.contacts?.map((contact, index) => {
          handleChange(`contacts[${index}].uiid`, contact.uiid || "");
          handleChange(
            `contacts[${index}].representativeName`,
            contact.representativeName
              ? contact.representativeName
              : "" || ""
          );
          handleChange(`contacts[${index}].jobTitle`, contact.jobTitle || "");
          handleChange(`contacts[${index}].contactType`, contact.contactType);
          handleChange(`contacts[${index}].gender`, contact.gender);
          handleChange(`contacts[${index}].email`, contact.email);
          handleChange(
            `contacts[${index}].contactNumberCountryCode`,
            contact.phone?.main?.code || ""
          );
          handleChange(
            `contacts[${index}].contactNumber`,
            contact.phone?.main?.number || ""
          );
          handleChange(
            `contacts[${index}].workPhoneCountryCode`,
            contact.phone?.work?.code || ""
          );
          handleChange(
            `contacts[${index}].workPhone`,
            contact.phone?.work?.number || ""
          );
          handleChange(
            `contacts[${index}].homePhoneCountryCode`,
            contact.phone?.home?.code || ""
          );
          handleChange(
            `contacts[${index}].homePhone`,
            contact.phone?.home?.number || ""
          );
          handleChange(`contacts[${index}].dateOfBirth`, contact.dob);
          handleChange(
            `contacts[${index}].addressLineOne`,
            contact.address?.address1 || ""
          );
          handleChange(
            `contacts[${index}].addressLineTwo`,
            contact.address?.address2 || ""
          );
          handleChange(
            `contacts[${index}].country`,
            contact.address?.country || ""
          );
          handleChange(
            `contacts[${index}].state`,
            contact.address?.state || ""
          );
          handleChange(`contacts[${index}].city`, contact.address?.city || "");
          handleChange(
            `contacts[${index}].zipCode`,
            contact.address?.zipCode || ""
          );
        }) || [];
      }
    }
    // }, [clientDetails]);
    // }, [Object.keys(clientDetails).length,clientProfileUiid]);
  }, []);

  const handleAddMore = () => {
    setFormData({
      contacts: [
        ...formData.contacts,
        {
          representativeName: "",
          jobTitle: "",
          contactType: "",
          gender: "",
          email: "",
          contactNumber: "",
          contactNumberCountryCode: "",
          workPhone: "",
          workPhoneCountryCode: "",
          homePhone: "",
          homePhoneCountryCode: "",
          dateOfBirth: "",
          addressLineOne: "",
          addressLineTwo: "",
          country: "",
          state: "",
          city: "",
          zipCode: "",
        },
      ],
    });
  };

  const handleRemove = (index) => {
    if (formData.contacts.length === 1) return;
    const updatedContacts = formData.contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, contacts: updatedContacts });
  };
  const handleSave = () => {
    if (
      formData.contacts.every(
        (contact) =>
          !contact.representativeName &&
          !contact.jobTitle &&
          !contact.contactType &&
          !contact.gender &&
          !contact.email &&
          !contact.contactNumber &&
          !contact.contactNumberCountryCode &&
          !contact.workPhone &&
          !contact.workPhoneCountryCode &&
          !contact.homePhone &&
          !contact.homePhoneCountryCode &&
          !contact.dateOfBirth &&
          !contact.addressLineOne &&
          !contact.addressLineTwo &&
          !contact.country &&
          !contact.state &&
          !contact.city &&
          !contact.zipCode
      )
    ) {
      setActiveTab((prev) => prev + 1);
      return;
    }
    const isValid = validateAllFields();
    if (!clientProfileUiid) {
      showToast("Business information must be saved before adding contacts", "error");
      return;
    }
    if (isValid) {
      const existingContacts = formData.contacts.filter(
        (contact) => contact.uiid
      );
      const newContacts = formData.contacts.filter((contact) => !contact.uiid);
      existingContacts.forEach((contact) => {
        dispatch(
          updateContactStart({
            coll: "Contact",
            uiid: contact.uiid,
            payload: [
              {
                type: "SET",
                value: {
                  phone: {
                    main: {
                      code: contact?.contactNumberCountryCode || undefined,
                      number: contact?.contactNumber || undefined,
                    },
                    work: {
                      code: contact?.workPhoneCountryCode || undefined,
                      number: contact?.workPhone || undefined,
                    },
                    home: {
                      code: contact.homePhoneCountryCode || undefined,
                      number: contact.homePhone || undefined,
                    },
                  },
                  representativeName: contact?.representativeName || undefined,
                  jobTitle: contact.jobTitle?.value ?? contact.jobTitle,
                  contactType:
                    contact.contactType?.value ?? contact.contactType,
                  gender: contact?.gender || undefined,
                  email: contact?.email || undefined,
                  dob: contact?.dateOfBirth || undefined,
                  address: {
                    address1: contact?.addressLineOne || undefined,
                    address2: contact?.addressLineTwo || undefined,
                    country: contact?.country || undefined,
                    state: contact?.state || undefined,
                    city: contact?.city || undefined,
                    zipCode: contact?.zipCode || undefined,
                  },
                },
              },
            ],
          })
        );
      });

      if (newContacts.length > 0) {
        const newContactsPayload = newContacts.map((contact) => ({
          addedBy: loginUserUiid,
          entityRefId: clientProfileUiid,
          entityType: "Client",
          phone: {
            main: {
              code: contact?.contactNumberCountryCode || undefined,
              number: contact?.contactNumber || undefined,
            },
            work: {
              code: contact?.workPhoneCountryCode || undefined,
              number: contact?.workPhone || undefined,
            },
            home: {
              code: contact?.homePhoneCountryCode || undefined,
              number: contact?.homePhone || undefined,
            },
          },
          representativeName: contact?.representativeName || undefined,
          jobTitle: contact?.jobTitle || undefined,
          contactType: contact?.contactType || undefined,
          gender: contact?.gender || undefined,
          email: contact?.email || undefined,
          dob: contact?.dateOfBirth || undefined,
          address: {
            address1: contact?.addressLineOne || undefined,
            address2: contact?.addressLineTwo || undefined,
            country: contact?.country || undefined,
            state: contact?.state || undefined,
            city: contact?.city || undefined,
            zipCode: contact?.zipCode || undefined,
            comments: contact?.addressLineTwo || undefined,
          },
        }));
        dispatch(
          addContactStart({
            submissionData: newContactsPayload,
            onSuccess: () => {
              dispatch(
                getClientOneStart({
                  clientUiid: clientProfileUiid,
                  retrieve: `contacts`,
                  ref: `Contact`,
                })
              );
            },
          })
        );
        setActiveTab((prev) => prev + 1);
      }
    } else {
      showToast("Please fill all the required fields before saving", "error", 5000);
    }
  };

  const handleDraft = useMemo(
    () =>
      debounce((value) => {
        dispatch(
          // addToDraft({
          //   [field]: value ? value : "",
          // })
          addToDraft({ ...value })
        );
      }, 1000),
    [dispatch]
  );
  useEffect(() => {
    let clientData = {
      contacts:
        formData?.contacts?.map((contact) => ({
          representativeName: contact.representativeName || "",
          jobTitle: contact.jobTitle || "",
          contactType: contact.contactType || "",
          gender: contact.gender || "",
          email: contact.email || "",
          phone: {
            main: {
              code: contact.contactNumberCountryCode || "",
              number: contact.contactNumber || "",
            },
            work: {
              code: contact.workPhoneCountryCode || "",
              number: contact.workPhone || "",
            },
            home: {
              code: contact.homePhoneCountryCode || "",
              number: contact.homePhone || "",
            },
          },
          dob: contact.dateOfBirth || "",
          address: {
            address1: contact.addressLineOne || "",
            address2: contact.addressLineTwo || "",
            country: contact.country || "",
            state: contact.state || "",
            city: contact.city || "",
            zipCode: contact.zipCode || "",
          },
        })) || [],
    };
    handleDraft(clientData);
  }, [formData]);

  return (
    <div className="flex flex-col min-h-screen px-8 py-3 rounded">
      <div className="mb-8">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle title={"Contacts"} icon={"badge"} tag="h5" />
            </div>
          }
          RightSection={
            <>
              {isEditable && editClient && (
                <>
                  {editClient !== "/client/client-details" && (
                    <Button
                      title="Back"
                      size="sm"
                      icon={"arrow_left_alt"}
                      onClick={handleBack}
                    />
                  )}
                  <Button title="Save" size="sm" onClick={handleSave} />
                </>
              )}
            </>
          }
        />
      </div>

      <div>
        <div className="flex justify-end">
          <Button
            title="+Add more"
            variant="text"
            color="info"
            onClick={handleAddMore}
            disabled={!isEditable === true}
          />
        </div>

        {formData.contacts.map((contact, index) => (
          <ContactComponent
            key={contact.uiid || index}
            contact={contact}
            index={index}
            formData={formData}
            errors={errors}
            handleRemove={handleRemove}
            handleChange={handleChange}
            isEditable={isEditable}
          />
        ))}
      </div>
    </div>
  );
};

export default Contacts;
